import React, { useState } from "react";
import NewLead from "./lead/NewLead";
import Chasing from "./lead/Chasing";
import CallBooked, { PrepPack } from "./lead/CallBooked";
import CallHeld from "./lead/CallHeld";
import PricingSent from "./lead/PricingSent";
import Deciding from "./lead/Deciding";
import Agreement from "./lead/Agreement";
import NewClient from "./lead/NewClient";
import Client from "./lead/Client";
import Churned from "./lead/Churned";
import Parked from "./lead/Parked";
import Lost from "./lead/Lost";
import OutLine from "./lead/OutLine";
import AssociatedPointer from "./lead/AssociatedPointer";
import { nextCallView, rangeBothZones } from "./lead/leadModel.mjs";
import { relativeDay, firstName } from "./format.mjs";
import { isConnected, connectHref } from "./linkedinConnect.mjs";
import useConnectedToggle from "./AlreadyConnected";

/*
 * THE LEAD BLOCK: the facts this situation turns on, chosen by the situation (the
 * heart of the record: the brief's situations table and matrix 1e). No action
 * buttons: Next owns them. One variant per situation, in record/lead/, every one
 * built in the drawn card grammar (frames 1a, 1c, 1d, 1g, 1i), and every sentence
 * in them worded by record/lead/leadModel.mjs, which the tests pin. This file only
 * dispatches and adds the two things that ride on top of any variant:
 *
 *   - A MEETING TODAY OR TOMORROW: a status line with Prep, in the lead block,
 *     never a Next line (the brief, "Next, per kind"; record.next.meeting from
 *     nextModel). Prep opens the prep pack under the line. At Call booked the block
 *     IS the call and its prep, so the line is left out rather than said twice.
 *   - AN ASSOCIATED COLLEAGUE: the company's own block (record.view.situation.
 *     company_situation). The pointer to the point of contact is drawn by the
 *     shell right under the header (frame 1i: "one plain line under the header"),
 *     because a reply owed can put the conversation above this block.
 *
 * Props:
 *   record   the shell's bundle: key (the situation), view.situation, next.meeting,
 *            act.facts, timeline.rows, automations, zones, and whatever the variant
 *            reads (each variant documents its own)
 *   ops      passed through to the variant
 */
export const VARIANTS = {
  new_first_email: NewLead,
  new_chasing: Chasing,
  call_booked: CallBooked,
  call_held: CallHeld,
  pricing_sent: PricingSent,
  deciding: Deciding,
  agreement: Agreement,
  new_client: NewClient,
  client: Client,
  churned: Churned,
  parked: Parked,
  lost: Lost,
  out: OutLine,
  associated: AssociatedPointer,
};

function MeetingLine({ record }) {
  const [prep, setPrep] = useState(false);
  const m = record.next && record.next.meeting;
  if (!m || !m.at) return null;
  const facts = (record.act && record.act.facts) || {};
  const call = nextCallView({ facts: { ...facts, next_meeting_at: m.at, next_meeting_title: m.title || facts.next_meeting_title }, rows: record.timeline.rows, automations: record.automations, company: record.company, now: record.now });
  const day = m.kind === "meeting_today" ? "today" : relativeDay(m.at, { now: record.now });
  return (
    <section className="rec-card rec-card--tight rec-lead rec-lead--meeting" aria-label="Meeting">
      <div className="rec-card__head">
        <span>
          <b className="rec-strong">Meeting {day}</b>
          {m.title ? <span className="rec-strong"> · {m.title}</span> : null}
          <span className="rec-sub"> · {rangeBothZones(m.at, call && call.end_at, record.zones)}</span>
          {call && call.join && (
            <>
              <span className="rec-sub"> · </span>
              <a className="rec-lead__link" href={call.join} target="_blank" rel="noopener noreferrer">Join ↗</a>
            </>
          )}
        </span>
        <button type="button" className="rec-link rec-lead__link" aria-expanded={prep} onClick={() => setPrep((p) => !p)}>
          {prep ? "Hide prep" : "Prep"}
        </button>
      </div>
      {/* Called, not mounted: PrepPack holds no state, and its null is what says
          there is nothing to prep from. */}
      {prep && (PrepPack({ record, call }) || <div className="rec-empty">Nothing on record to prep from yet.</div>)}
    </section>
  );
}

/*
 * "CONNECT ON LINKEDIN", the lead block's one exception to "Next owns every
 * button" (Gabriel, 2026-10-26): when LinkedIn's mail has never said we are
 * connected to the person this record is about. With "Already connected" beside
 * it (2026-11-02) for the invitation he accepted, which no email reports. A slim
 * card above the stage's own, like the meeting line, so every stage carries it
 * without each variant growing a copy. Not on an out-of-pipeline lead (nobody
 * to court) or an associated pointer (the lead is someone else).
 */
function ConnectLine({ record, shownAs, ops }) {
  const person = record.contact;
  const toggle = useConnectedToggle(person, ops);
  if (!person || shownAs === "out" || record.key === "associated" || isConnected(person)) return null;
  const href = connectHref(person, record.company);
  if (!href) return null;
  const first = firstName(person) || "them";
  // Never "not connected": no email saying so is all we know (linkedinConnect.mjs).
  return (
    <section className="rec-card rec-card--tight rec-lead rec-lead--connect" aria-label="LinkedIn">
      <div className="rec-card__head">
        <span className="rec-sub">No LinkedIn connection with {first} on record</span>
        <span className="rec-lead__connect-acts">
          {ops && (
            <button type="button" className="rec-link" disabled={toggle.busy} onClick={() => toggle.set(true)}>
              Already connected
            </button>
          )}
          <a className="rec-btn rec-btn--li" href={href} target="_blank" rel="noopener noreferrer">Connect on LinkedIn</a>
        </span>
      </div>
      {toggle.error && <div className="rec-err">{toggle.error}</div>}
    </section>
  );
}

export default function LeadBlock({ record, ops }) {
  const key = record.key;
  const Variant = VARIANTS[key] || null;
  const company = record.view.situation.company_situation;
  const Company = key === "associated" && company && company !== "out" ? VARIANTS[company] : null;
  const shownAs = Company ? company : key;
  return (
    <div id="rec-lead-block" className="rec__col">
      {shownAs !== "call_booked" && shownAs !== "out" && <MeetingLine record={record} />}
      <ConnectLine record={record} shownAs={shownAs} ops={ops} />
      {Variant && key !== "associated" && <Variant record={record} ops={ops} />}
      {Company && <Company record={{ ...record, key: company }} ops={ops} />}
    </div>
  );
}
